// /style-guide: every building block (components/ui/), the board's own pieces, the icons and every
// token group, in either brand and either mode. The brand and mode switches set data-brand and
// data-theme on this page's own root only (a theme scope, tokens.css): the deployment's brand
// (BOARD_THEME) and the viewer's theme preference are left alone.
import { useId, useRef, useState, type ReactNode } from 'react';
import type { Checks } from '../api/client';
import { BadgeTag, COLUMN_ORDER, ChecksSummary, EpicTag, LabelTag, ReviewText, StatusPill, tagClass } from '../components/Bits';
import { ICON_NAMES, Icon } from '../components/Icon';
import { Mark } from '../components/Shell';
import {
  Badge,
  Bars,
  Button,
  Callout,
  Card,
  ConfirmDialog,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogTitle,
  EmptyState,
  ExpandableRow,
  InfoTip,
  Input,
  Textarea,
  Meter,
  PageHeader,
  Select,
  Sparkline,
  Spinner,
  Stat,
  Switch,
  TabPanel,
  Table,
  Tabs,
  TagInput,
  Td,
  Th,
  cn,
  mono,
  type BadgeTone,
  type ButtonVariant,
  type CalloutTone,
  type CardEdge,
} from '../components/ui';
import { BRANDS, DEFAULT_BRAND, PAIRS, TOKENS, type Brand, type TokenInfo } from '../styles/tokens.gen';

type Mode = 'system' | 'light' | 'dark';
const BADGES = ['needs-report', 'hold', 'duplicate', 'unparsed', 'parked', 'release', 'draft'] as const;
const run = (state: 'pass' | 'fail' | 'pending') => ({ name: state, state, url: null });
const SAMPLE_CHECKS: Checks[] = [
  { state: 'pass', total: 3, passed: 3, failed: 0, pending: 0, runs: [run('pass'), run('pass'), run('pass')] },
  { state: 'fail', total: 3, passed: 2, failed: 1, pending: 0, runs: [run('pass'), run('fail'), run('pass')] },
  { state: 'pending', total: 3, passed: 2, failed: 0, pending: 1, runs: [run('pass'), run('pass'), run('pending')] },
];
// Labels take their colour from the meta repo's roles (repos.yaml `labels`): these are the defaults.
const SAMPLE_ROLES = { bug: 'bug', needs_triage: 'needs-triage', agent_ready: 'agent-ready', hold: 'hold' };

/** The building blocks, in the order the guide shows them (a test checks each has its section). */
export const BLOCKS = [
  'Button',
  'Badge',
  'Card',
  'Stat',
  'Callout',
  'EmptyState',
  'PageHeader',
  'Table',
  'Tabs',
  'Switch',
  'InfoTip',
  'TagInput',
  'Input',
  'Textarea',
  'Select',
  'Dialog',
  'Spinner',
  'Meter',
  'Sparkline',
  'Bars',
] as const;

/** Colour token groups, by name prefix (the first that matches). */
const GROUPS: [title: string, test: (name: string) => boolean][] = [
  ['Surfaces and ink', (n) => /^(page|surface|hairline|ink-|scrim)/.test(n)],
  ['Accent and action', (n) => /^(accent|action|focus)/.test(n)],
  ['States', (n) => /^(success|warning|danger|signal)/.test(n)],
  ['Lifecycle columns', (n) => n.startsWith('status-')],
  ['Checks', (n) => n.startsWith('checks-')],
  ['Badges and labels', (n) => /^(badge|label)-/.test(n)],
];

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');

function Section({ title, id, children, note }: { title: string; id?: string; children: ReactNode; note?: ReactNode }) {
  const h = `h-${id ?? slug(title)}`;
  return (
    <section id={id ?? slug(title)} aria-labelledby={h} className="grid min-w-0 gap-3 border-t border-hairline pt-5">
      <h2 id={h} className="m-0 text-[1.125rem]">
        {title}
      </h2>
      {note && <p className="m-0 text-ink-muted">{note}</p>}
      {children}
    </section>
  );
}

const ROW = 'flex flex-wrap items-center gap-2';

function Segmented<T extends string>({
  legend,
  name,
  options,
  value,
  onChange,
}: {
  legend: string;
  name: string;
  options: readonly T[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <fieldset className="m-0 flex flex-wrap items-center gap-2 border-0 p-0">
      <legend className="float-left mr-2 text-ink-muted">{legend}</legend>
      {options.map((o) => (
        <label
          key={o}
          className={cn(
            'inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded border px-3',
            value === o ? 'border-accent' : 'border-hairline-strong',
          )}
        >
          <input type="radio" name={name} value={o} checked={value === o} onChange={() => onChange(o)} />
          <span>{o}</span>
        </label>
      ))}
    </fieldset>
  );
}

function DialogDemo() {
  const [open, setOpen] = useState<'plain' | 'confirm' | null>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  const id = useId();
  return (
    <div className={ROW}>
      <Button onClick={() => setOpen('plain')}>Open a dialog</Button>
      <Button variant="danger" onClick={() => setOpen('confirm')}>
        Remove a repo…
      </Button>
      {open === 'plain' && (
        <Dialog onClose={() => setOpen(null)} labelledBy={`${id}-h`} initialFocus={cancel}>
          <DialogBody>
            <DialogTitle id={`${id}-h`}>Merge #42 into main?</DialogTitle>
            <p className="m-0">A dialog: Escape or a click outside cancels it; Cancel has the focus.</p>
          </DialogBody>
          <DialogFooter>
            <Button ref={cancel} onClick={() => setOpen(null)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => setOpen(null)}>
              Merge
            </Button>
          </DialogFooter>
        </Dialog>
      )}
      {open === 'confirm' && (
        <ConfirmDialog
          title="Remove acme/acme-meta?"
          confirmText="acme-meta"
          action="Remove"
          onCancel={() => setOpen(null)}
          onConfirm={() => setOpen(null)}
        >
          <p className="m-0">Its cards leave the board. The repo itself is not touched.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

function TokenTable({ tokens, brand }: { tokens: TokenInfo[]; brand: Brand }) {
  return (
    <Table>
      <thead>
        <tr>
          <Th>Swatch</Th>
          <Th>Token</Th>
          <Th>Light</Th>
          <Th>Dark</Th>
        </tr>
      </thead>
      <tbody>
        {tokens.map((t) => (
          <tr key={t.name}>
            <Td>
              <span className="inline-block h-5 w-8 rounded-[4px] border border-hairline-strong" style={{ background: `var(--${t.name})` }} />
            </Td>
            <Td className={mono}>--{t.name}</Td>
            <Td className={mono}>{t.values[brand].light}</Td>
            <Td className={mono}>{t.values[brand].dark}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export function StyleGuide() {
  const [brand, setBrand] = useState<Brand>(() => {
    const b = document.documentElement.dataset.brand as Brand | undefined;
    return b && BRANDS.includes(b) ? b : DEFAULT_BRAND;
  });
  const [mode, setMode] = useState<Mode>('system');
  const [tab, setTab] = useState('open');
  const [tags, setTags] = useState(['docker', 'ios']);
  const [on, setOn] = useState(true);
  const [sel, setSel] = useState('viewer');

  const colours = TOKENS.filter((t) => t.type === 'color');
  const others = TOKENS.filter((t) => t.type !== 'color');

  return (
    <main
      data-brand={brand}
      data-theme={mode === 'system' ? undefined : mode}
      className="min-h-screen bg-page text-[14px] text-ink-body"
    >
      <div className="mx-auto grid max-w-[64rem] gap-5 p-4">
        <PageHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Mark />
              Style guide
            </span>
          }
          purpose={
            <>
              The building blocks and design tokens, brand <strong>{brand}</strong>. Components use only semantic tokens
              (tokens/*.json, built by tools/tokens.py into Tailwind's theme).
            </>
          }
          actions={<a href="/">Home</a>}
        />

        <div className="grid gap-2">
          <Segmented legend="Brand" name="brand" options={BRANDS} value={brand} onChange={setBrand} />
          <Segmented legend="Mode" name="mode" options={['system', 'light', 'dark'] as const} value={mode} onChange={setMode} />
        </div>

        <Section title="Button" id="button">
          {(['primary', 'secondary', 'ghost', 'danger'] as ButtonVariant[]).map((v) => (
            <div key={v} className={ROW}>
              <Button variant={v}>{v}</Button>
              <Button variant={v} size="sm">
                small
              </Button>
              <Button variant={v} size="icon" aria-label={`${v} icon button`}>
                <Icon name="refresh" />
              </Button>
              <Button variant={v} disabled>
                disabled
              </Button>
            </div>
          ))}
          <div className={ROW}>
            <Button variant="primary" size="lg">
              Large primary
            </Button>
          </div>
        </Section>

        <Section title="Badge" id="badge">
          <div className={ROW}>
            {(['success', 'signal', 'warning', 'danger', 'muted', 'accent'] as BadgeTone[]).map((t) => (
              <Badge key={t} tone={t}>
                {t}
              </Badge>
            ))}
            <Badge tone="signal" spinner>
              running
            </Badge>
          </div>
        </Section>

        <Section title="Card" id="card">
          <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
            {(['none', 'accent', 'signal', 'success', 'warning', 'danger'] as CardEdge[]).map((e) => (
              <Card key={e} edge={e} padding="sm">
                <strong>edge {e}</strong>
                <p className="mb-0 mt-1 text-ink-muted">A bordered surface.</p>
              </Card>
            ))}
          </div>
        </Section>

        <Section title="Stat" id="stat" note="A zero is always neutral.">
          <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-3">
            <Stat value={12} label="open PRs" />
            <Stat value={3} label="failing" tone="danger" />
            <Stat value={0} label="failing" tone="danger" />
            <Stat value={5} label="pending" tone="warning" />
            <Stat value={41} label="shipped" tone="success" />
            <Stat value={2} label="runners" tone="signal" />
          </div>
        </Section>

        <Section title="Callout" id="callout">
          {(['neutral', 'info', 'success', 'warning', 'danger'] as CalloutTone[]).map((t) => (
            <Callout key={t} tone={t} icon={<Icon name="warn" small />} title={t}>
              A short message about something that needs saying.
            </Callout>
          ))}
          <Callout tone="danger" outline icon={<Icon name="x" small />}>
            Outline: merge failed: the head moved.
          </Callout>
          <Callout tone="success" outline icon={<Icon name="merge" small />}>
            Outline: merged #42 into main.
          </Callout>
        </Section>

        <Section title="EmptyState" id="emptystate">
          <EmptyState title="Nothing in flight" action={<Button size="sm">Refresh</Button>}>
            Plans move here when their branch opens a PR.
          </EmptyState>
          <EmptyState size="sm">Nothing here</EmptyState>
        </Section>

        <Section title="PageHeader" id="pageheader">
          <Card padding="sm">
            <PageHeader title="Fleet" purpose="Every runner and what it is doing." actions={<Button variant="primary">Add runner</Button>} />
          </Card>
        </Section>

        <Section title="Table" id="table" note="It scrolls sideways inside its own box; a row can expand.">
          <Table>
            <thead>
              <tr>
                <Th>
                  <span className="sr-only">Expand</span>
                </Th>
                <Th>Runner</Th>
                <Th>State</Th>
                <Th>Job</Th>
                <Th>Last seen</Th>
              </tr>
            </thead>
            <tbody>
              <ExpandableRow
                label="orca-vps details"
                cells={[<span className={mono}>orca-vps</span>, <Badge tone="success">idle</Badge>, '—', '2 min ago']}
                detail="Labels: self-hosted, shipyard. 4 CPUs, 8 GB."
              />
              <ExpandableRow
                label="hermes details"
                defaultOpen
                cells={[
                  <span className={mono}>hermes</span>,
                  <Badge tone="signal" spinner>
                    busy
                  </Badge>,
                  <span className={mono}>check #512</span>,
                  'now',
                ]}
                detail="Running make check for terencefaul/shipyard#88."
              />
            </tbody>
          </Table>
        </Section>

        <Section title="Tabs" id="tabs">
          <Tabs
            label="Pull requests"
            idPrefix="sg-seg"
            tabs={[
              { id: 'open', label: 'Open', hint: '4' },
              { id: 'merged', label: 'Merged', hint: '12' },
              { id: 'all', label: 'All' },
            ]}
            value={tab}
            onValueChange={setTab}
          />
          <TabPanel id={tab} idPrefix="sg-seg" className="text-ink-muted">
            Showing: {tab}
          </TabPanel>
          <Tabs
            label="Sections"
            variant="underline"
            idPrefix="sg-ul"
            tabs={[
              { id: 'open', label: 'Overview' },
              { id: 'merged', label: 'Checks', hint: '3' },
              { id: 'all', label: 'Activity' },
            ]}
            value={tab}
            onValueChange={setTab}
          />
        </Section>

        <Section title="Switch" id="switch">
          <label className={ROW}>
            <Switch checked={on} onChange={setOn} aria-label="Auto-refresh" />
            Auto-refresh {on ? 'on' : 'off'}
          </label>
          <label className={ROW}>
            <Switch checked={false} disabled aria-label="Disabled switch" />
            Disabled
          </label>
        </Section>

        <Section title="InfoTip" id="infotip" note="An (i) beside a setting's label: hover, focus or tap opens it; Escape or a tap elsewhere closes it.">
          <span className={ROW}>
            Most rounds per item
            <InfoTip label="Most rounds per item" yamlKey="max_rounds" fallback="3">
              Worker rounds per item, the first one included. Send-backs stop there and ask you.
            </InfoTip>
          </span>
        </Section>

        <Section title="TagInput" id="taginput" note="Names as chips; Enter, a comma or leaving the field adds one.">
          <TagInput label="has" value={tags} onChange={setTags} className="max-w-[400px]" />
          <TagInput label="protect" value={[]} placeholder="none" onChange={() => undefined} className="max-w-[400px]" />
        </Section>

        <Section title="Input" id="input">
          <Input aria-label="Meta repo" placeholder="owner/name (monospace)" className="max-w-[300px]" />
          <Input aria-label="Email" sans placeholder="terica email (sans)" className="max-w-[300px]" />
          <Input aria-label="Disabled" disabled value="read-only" className="max-w-[300px]" />
        </Section>

        <Section title="Textarea" id="textarea">
          <Textarea aria-label="YAML source" defaultValue={'in_flight_cap: 3\nplacement: local-first'} className="max-w-[400px]" spellCheck={false} />
        </Section>

        <Section title="Select" id="select">
          <div className={ROW}>
            <Select aria-label="Role" value={sel} onChange={(e) => setSel(e.target.value)}>
              <option value="viewer">viewer</option>
              <option value="owner">owner</option>
            </Select>
            <Select aria-label="Repo" lead={<span className="text-ink-muted">repo</span>}>
              <option>all (3)</option>
              <option>acme/acme-meta</option>
            </Select>
          </div>
        </Section>

        <Section title="Dialog" id="dialog" note="The confirm-by-typing variant keeps its action disabled until the text matches.">
          <DialogDemo />
        </Section>

        <Section title="Spinner" id="spinner">
          <div className={ROW}>
            <Spinner label="Loading" />
            <span className="inline-flex items-center gap-1.5 text-ink-muted">
              <Spinner /> syncing…
            </span>
          </div>
        </Section>

        <Section title="Meter" id="meter" note="The bar turns warning at its threshold (80% unless set).">
          <div className="grid max-w-[320px] gap-3">
            <Meter label="Runners busy" value={3} max={8} />
            <Meter label="Disk" value={7} max={8} />
            <Meter label="Unreported" value={2} max={3} warnAt={3} />
          </div>
        </Section>

        <Section title="Sparkline" id="sparkline">
          <div className={ROW}>
            <span className="text-accent-display">
              <Sparkline values={[3, 5, 4, 8, 6, 9, 12]} label="Merges per week, rising to 12" />
            </span>
            <span className="text-checks-fail-ink">
              <Sparkline values={[1, 0, 2, 4, 3, 5]} label="Failing checks, rising to 5" />
            </span>
            <span className="text-ink-muted">
              <Sparkline values={[5, 5, 5, 5]} label="Flat at 5" width={64} />
            </span>
          </div>
        </Section>

        <Section title="Bars" id="bars" note="One bar per run of a CI job: height is how long it took; success, or danger when it did not pass.">
          <div className="grid max-w-[320px] gap-1">
            <Bars
              bars={[420, 455, 430, 470, 900, 440, 452, 461, 438, 470, 445, 449].map((v, i) => ({ value: v, ok: i !== 4 && i !== 9 }))}
              label="web: 12 runs, 2 did not pass; one took twice as long"
            />
            <span className="text-[12px] text-ink-muted">83% passed (10 of 12)</span>
          </div>
        </Section>

        <Section title="Board pieces" id="board">
          <div className={ROW}>
            {COLUMN_ORDER.map((s) => (
              <StatusPill key={s} column={s} />
            ))}
          </div>
          <div className={ROW}>
            {SAMPLE_CHECKS.map((c) => (
              <ChecksSummary key={c.state} checks={c} />
            ))}
            <ReviewText review="approved" />
            <ReviewText review="changes_requested" />
          </div>
          <div className={ROW}>
            {BADGES.map((b) => (
              <BadgeTag key={b} badge={b} />
            ))}
            <LabelTag name="bug" roles={SAMPLE_ROLES} />
            <LabelTag name="needs-triage" roles={SAMPLE_ROLES} />
            <LabelTag name="hold" roles={SAMPLE_ROLES} />
            <LabelTag name="agent-ready" roles={SAMPLE_ROLES} />
            <EpicTag epic="epic" />
            <span className={tagClass('badge-batch')}>B-001</span>
            <span className={tagClass('kind')}>Plan</span>
          </div>
        </Section>

        <Section
          title="Icons"
          id="icons"
          note="Every icon at 16px and at the small size (12px), drawn in currentColor on a 16px grid. No two should read alike; an icon-only control also needs an aria-label."
        >
          <ul className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-2 p-0" aria-label="Icons">
            {ICON_NAMES.map((name) => (
              <li
                key={name}
                className="flex flex-col items-center gap-1.5 rounded border border-hairline px-1 py-2.5"
                data-icon={name}
              >
                <span className="flex items-center gap-2.5">
                  <Icon name={name} px={16} />
                  <Icon name={name} small />
                </span>
                <span className={mono}>{name}</span>
              </li>
            ))}
          </ul>
        </Section>

        {GROUPS.map(([title, test]) => (
          <Section key={title} title={`Colour tokens: ${title}`} id={`tokens-${slug(title)}`}>
            <TokenTable tokens={colours.filter((t) => test(t.name))} brand={brand} />
          </Section>
        ))}

        <Section title="Type, radius and the rest" id="tokens-other">
          <p className="m-0 font-sans text-[15px]">Inter: the quick brown fox jumps over the lazy dog.</p>
          <p className="m-0 font-mono text-[13px]">JetBrains Mono: P-005 · terencefaul/shipyard#88 · 0O1lI</p>
          <ul className="m-0 grid list-none gap-0 p-0">
            {others.map((t) => (
              <li key={t.name} className={cn(mono, 'border-b border-hairline py-2')}>
                --{t.name}: {t.values[brand].light}
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Contrast pairs (WCAG AA)" id="pairs" note={`Brand ${brand}; every pair is checked in every brand by make check.`}>
          <Table>
            <thead>
              <tr>
                <Th>Sample</Th>
                <Th>Pair</Th>
                <Th>Needs</Th>
                <Th>Light</Th>
                <Th>Dark</Th>
              </tr>
            </thead>
            <tbody>
              {PAIRS.map((p) => (
                <tr key={`${p.fg}/${p.bg}`}>
                  <Td>
                    <span
                      className="inline-block min-w-10 rounded-[4px] border border-hairline px-2 py-0.5 text-center font-semibold"
                      style={{ color: `var(--${p.fg})`, background: `var(--${p.bg})` }}
                    >
                      Aa
                    </span>
                  </Td>
                  <Td className={mono}>
                    --{p.fg} on --{p.bg}
                  </Td>
                  <Td>
                    {p.min}:1 {p.kind}
                  </Td>
                  <Ratio value={p.ratios[brand].light} min={p.min} />
                  <Ratio value={p.ratios[brand].dark} min={p.min} />
                </tr>
              ))}
            </tbody>
          </Table>
        </Section>
      </div>
    </main>
  );
}

function Ratio({ value, min }: { value: number; min: number }) {
  const ok = value >= min;
  return (
    <Td className={cn('whitespace-nowrap', !ok && 'font-bold')}>
      {ok ? '✓' : '✕'} {value}:1 {ok ? 'pass' : 'fail'}
    </Td>
  );
}
